import emptyStyle from "../../../../../Common/emptyState.module.css";
import loggerStyle from "../../commandLogger.module.css";

import { AuthContext } from "../../../../../../contexts/AuthContext.ts";
import { PentestContext } from "../../../../../../contexts/PentestContext.ts";
import { useContext } from "react";
import { Vulnerability } from "../../../../../../sdkStructs.ts";
import VulnBlock from "./components/VulnBlock/VulnBlock.tsx";
import InfoHover from "../../../../../Common/Tooltips/InformationHover/InformationHover.tsx";
import { LoggerContext } from "../../../../../../contexts/LoggerContext.ts";
import { Plus, Tag, Zap } from "lucide-react";
import GenerateWidget from "./components/GenerateWidget/GenerateWidget.tsx";
import { AppContext } from "../../../../../../contexts/AppContext.ts";
import GenerateVulnBlock from "./components/GenerateVulnBlock/GenerateVulnBlock.tsx";
import { addVuln, createAssociationById } from "../../../../../Common/createVuln.ts";

function VulnWidget() {
  const token = useContext(AuthContext).token;
  const { openVulnModal, pentestId, generating, selectVulnToGenerate, selectRemainingVulnsToGenerate, pentestVulns, refreshVulns } = useContext(PentestContext);

  const { setLoading, setShowModal } = useContext(AppContext);

  const { flagMode, toggleFlagMode, selectedIOBlocks, setSelectedIOBlocks, emptyLogger } = useContext(LoggerContext);

  // STATE MANAGEMENT
  function resetState() {
    toggleFlagMode(false);
    refreshVulns();
    setSelectedIOBlocks([]);
  }

  async function selectVulnToAdd(data: Vulnerability) {
    setLoading(true);
    await createAssociationById(token, data.ID, selectedIOBlocks);
    resetState();
    setLoading(false);
  }

  async function createNewVuln(name: string) {
    setLoading(true);
    await addVuln(token, selectedIOBlocks, name, pentestId);
    resetState();
    setLoading(false);
  }
  async function confirmCreateNewVuln() {
    setShowModal("name", {
      title: "Vuln Name",
      submit: createNewVuln,
      close: () => setShowModal("none", undefined),
    })
  }

  function getPlus() {
    if (!flagMode) return;
    return (
      <button className={loggerStyle.plusWrapper} onClick={confirmCreateNewVuln}>
        <Plus className={loggerStyle.plusImage} stroke="var(--onedark-text)" strokeWidth="2" />
      </button>
    );
  }

  function getVulns() {
    if (pentestVulns.length === 0 && !flagMode)
      return (
        <div className={emptyStyle.emptyLogger}>
          <Zap className={emptyStyle.emptyStateImage} stroke="var(--onedark-text)" strokeWidth="1.5" />
          {emptyLogger ? (
            <></>
          ) : (
            <>
              <p className={emptyStyle.emptyTitle + " " + loggerStyle.smallerFont}>You haven't added any vulns!</p>
              <p className={emptyStyle.emptyText + " " + loggerStyle.smallerFont}>
                First, tag some commands using the <Tag strokeWidth={1.5} /> icon.
              </p>
            </>
          )}
        </div>
      );

    return (
      <div className={loggerStyle.reverseList}>
        {pentestVulns.map((vuln, orderNumber) =>
          !generating[vuln.ID] ? (
            <VulnBlock
              key={vuln.ID}
              data={vuln}
              orderNumber={orderNumber + 1}
              selectFunc={selectVulnToGenerate}
              openFunc={() => {
                openVulnModal(vuln, orderNumber + 1);
              }}
              addFunc={selectVulnToAdd}
            />
          ) : (
            <GenerateVulnBlock key={vuln.ID} data={vuln} orderNumber={orderNumber + 1} />
          ),
        )}
      </div>
    );
  }

  return (
    <div className={loggerStyle.listWrapper + " " + loggerStyle.vulnListWrapper}>
      <div className={loggerStyle.listOptions}>
        <h3>Vulnerabilities</h3>
        <InfoHover description="Collections of commands that reveal the same vulnerability. Each will be generated a paragraph in the final report." />
      </div>
      <div className={loggerStyle.listElements}>
        {getPlus()}
        {getVulns()}
      </div>

      <GenerateWidget vulns={pentestVulns} generateSections={() => selectRemainingVulnsToGenerate()} />
    </div>
  );
}

export default VulnWidget;
